import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Circle, Upload, Send, Eye, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, messageErreur } from "@/lib/api";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const CANAUX = [["sms", "SMS"], ["whatsapp", "WhatsApp"], ["email", "E-mail"]];

const ouvrir = (d) => api.get(`/documents/${d.id}/fichier`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank")).catch((e) => toast.error(messageErreur(e)));

const Doc = ({ d }) => (
  <div className="flex items-center justify-between gap-2 text-sm pl-6" data-testid={`document-${d.id}`}>
    <div className="min-w-0">
      <p className="truncate">{d.nom_fichier}</p>
      <p className="text-xs flex items-center gap-1 flex-wrap" data-testid={`document-ai-${d.id}`} style={{ color: d.analyse_ia?.lisible === false ? "var(--danger)" : "var(--muted)" }}>
        <Sparkles size={11} />{d.analyse_ia?.statut === "lu" ? `${d.analyse_ia.lisible ? "Lisible" : "Illisible"} · dossier ${d.analyse_ia.dossier_complet ? "complet" : "incomplet"} · ${d.analyse_ia.commentaire}` : "Pas encore lu par l'IA"} · {d.source === "client" ? "envoyé par le client" : "ajouté au CRM"}
      </p>
      {d.analyse_ia?.champs?.length > 0 && <p className="text-xs at-muted break-words">Lu : {d.analyse_ia.champs.map((c) => `${c.label} ${c.valeur}`).join(" · ")} (à ranger depuis « Mon assistant »)</p>}
    </div>
    <button type="button" onClick={() => ouvrir(d)} className="at-icon-btn shrink-0" aria-label={`Ouvrir ${d.nom_fichier}`} data-testid={`document-open-${d.id}`}><Eye size={15} /></button>
  </div>
);

export function DocumentsFiche({ fiche, onMaj }) {
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [canal, setCanal] = useState("sms");
  const [piece, setPiece] = useState("");
  const input = useRef(null);
  const charger = useCallback(() => api.get(`/fiches/${fiche.id}/documents`).then((r) => setE(r.data)).catch(() => {}), [fiche.id]);
  useEffect(() => { charger(); }, [charger]);
  if (!e || user.role === "partenaire" && !e.pieces.length) return null;
  const peut = user.role !== "partenaire";
  const lien = () => api.post(`/fiches/${fiche.id}/lien-depot`, { canal }).then(() => { toast.success(`Lien de dépôt envoyé par ${CANAUX.find((c) => c[0] === canal)[1]} (simulation).`); charger(); onMaj(); }).catch((er) => toast.error(messageErreur(er)));
  const envoyer = (fichier) => {
    const fd = new FormData(); fd.append("fichier", fichier); fd.append("piece_id", piece);
    api.post(`/fiches/${fiche.id}/documents`, fd).then(() => { toast.success("Document ajouté."); charger(); onMaj(); }).catch((er) => toast.error(messageErreur(er)));
  };
  return (
    <section className="at-card p-4 space-y-3 min-w-0" data-testid="fiche-documents">
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold">Pièces du dossier ({e.pieces.filter((p) => p.fournie).length}/{e.pieces.length})</p>
        {e.jours_restants !== null && <span className="text-xs font-bold at-pill px-2.5 py-1 shrink-0" style={{ background: e.jours_restants <= 2 ? "color-mix(in srgb, var(--danger) 16%, transparent)" : "var(--surface-2)", color: e.jours_restants <= 2 ? "var(--danger)" : "var(--ink)" }} data-testid="documents-countdown">J-{e.jours_restants} avant clôture</span>}
      </div>
      <p className="text-xs at-muted">Service au partenaire : le cabinet rassemble les pièces, il ne dépose pas le dossier CEE.</p>
      {e.pieces.map((p) => (
        <div key={p.id} className="space-y-1" data-testid={`piece-${p.id}`}>
          <p className="flex items-center gap-2 text-sm font-semibold">{p.fournie ? <Check size={16} style={{ color: "var(--success)" }} /> : <Circle size={16} className="at-muted" />}{p.libelle}</p>
          {p.documents.map((d) => <Doc key={d.id} d={d} />)}
        </div>
      ))}
      {e.autres.map((d) => <Doc key={d.id} d={d} />)}
      {fiche.infos_documents && Object.keys(fiche.infos_documents).length > 0 && (
        <div className="at-soft p-3 text-xs space-y-0.5" data-testid="documents-infos">
          <p className="font-bold text-sm">Infos rangées par l'IA</p>
          {Object.entries(fiche.infos_documents).map(([k, v]) => <p key={k}>{k.replace(/_/g, " ")} : <b>{v}</b></p>)}
        </div>
      )}
      {e.demandes_le && <p className="text-xs at-muted" data-testid="documents-reminders">Demandé le {new Date(e.demandes_le).toLocaleDateString("fr-FR")} · relances J+{e.relances_jours.join(", J+")} · faites : {e.relances_faites.length ? e.relances_faites.map((r) => (r === "finale" ? "finale" : `J+${r}`)).join(", ") : "aucune"}</p>}
      {peut && fiche.statut === "en_cours" && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {CANAUX.map(([c, l]) => <button key={c} type="button" onClick={() => setCanal(c)} className="at-btn !px-2 text-sm" style={{ background: canal === c ? "var(--accent)" : "var(--surface-2)", color: canal === c ? "var(--accent-ink)" : "var(--ink)" }} data-testid={`deposit-channel-${c}`}>{l}</button>)}
          </div>
          <button type="button" onClick={lien} className="at-btn at-btn-primary w-full" data-testid="send-deposit-link"><Send size={15} />Envoyer le lien de dépôt au client</button>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <ChoixListe valeur={piece || "_autre"} onChange={(v) => setPiece(v === "_autre" ? "" : v)} options={[{ code: "_autre", label: "Autre document" }, ...e.pieces.map((p) => ({ code: p.id, label: p.libelle }))]} testId="document-piece-select" />
            <button type="button" onClick={() => input.current?.click()} className="at-btn at-btn-ghost" data-testid="document-upload-btn"><Upload size={15} />Ajouter</button>
            <input ref={input} type="file" accept="image/*,application/pdf" className="hidden" onChange={(ev) => ev.target.files[0] && envoyer(ev.target.files[0])} data-testid="document-upload-input" />
          </div>
        </div>
      )}
    </section>
  );
}
